// src/business/pages/BusinessLogin.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "../../utils/apparifySwal";
import { useBusinessAuth } from "../context/BusinessAuthContext";
import PasswordInput from "../../components/PasswordInput";
import ThemeToggle from "../../components/ThemeToggle";
import { getLoginErrorDialogOptions } from "../../utils/loginError";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function BusinessLogin() {
  const { login, isAuthenticated, businessId, loading } = useBusinessAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from || "/business";

  useEffect(() => {
    document.body.classList.add("business-portal-active");
    return () => document.body.classList.remove("business-portal-active");
  }, []);

  // If already authenticated WITH a business membership, skip login page
  useEffect(() => {
    if (loading) return;
    if (isAuthenticated && businessId) {
      navigate(from, { replace: true });
    }
    // Do NOT redirect non-members here — let them see the login form
    // and only redirect after they submit and we confirm no membership.
  }, [loading, isAuthenticated, businessId, navigate, from]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const swalBase = useMemo(
    () => ({
      target: "#business-swal-root",
    }),
    []
  );

  function validate() {
    if (!EMAIL_RE.test(email.trim())) return "Enter a valid email.";
    if (!password || password.length < 4) return "Password is required.";
    return "";
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (submitting) return;

    const err = validate();
    if (err) {
      await Swal.fire({
        ...swalBase,
        ...getLoginErrorDialogOptions(null, err),
      });
      return;
    }

    setSubmitting(true);

    try {
      // Call the real backend login and write session to localStorage
      await login({ email: email.trim(), password });

      // ✅ LOGIN SUCCESS SWAL
      await Swal.fire({
        ...swalBase,
        icon: "success",
        title: "Welcome!",
        html: "You are now signed in.",
        timer: 1200,
        showConfirmButton: false,
      });

      // Re-read session to check if user actually has a business membership
      // (login() already fetched this — check the updated context)
      const session = JSON.parse(localStorage.getItem("business_session") || "null");
      if (!session?.businessId) {
        console.warn(`[BusinessLogin] auth: redirect non-member userId=${session?.user?.id} to /`);
        await Swal.fire({
          ...swalBase,
          icon: "warning",
          title: "No business found",
          html: "Your account is not linked to any business.<br/>Redirecting to homepage.",
          timer: 2000,
          showConfirmButton: false,
        });
        navigate("/", { replace: true });
        return;
      }

      navigate(from, { replace: true });
    } catch (loginError) {
      await Swal.fire({
        ...swalBase,
        ...getLoginErrorDialogOptions(loginError),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="business-portal relative min-h-screen overflow-hidden bg-[#f5f5f4] text-neutral-900 dark:bg-[#0a0a0a] dark:text-neutral-50">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div
          className="absolute inset-0 opacity-70 dark:hidden"
          style={{
            backgroundImage:
              "linear-gradient(rgba(23, 23, 23, 0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(23, 23, 23, 0.045) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div
          className="absolute inset-0 hidden opacity-80 dark:block"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        <div className="absolute left-1/2 top-1/2 h-[28rem] w-[28rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/80 blur-3xl dark:bg-white/[0.06] sm:h-[36rem] sm:w-[36rem]" />
        <div className="absolute -left-32 -top-32 h-72 w-72 rounded-full border border-neutral-900/10 dark:border-white/10 sm:h-96 sm:w-96" />
        <div className="absolute -left-20 -top-20 h-72 w-72 rounded-full border border-neutral-900/5 dark:border-white/5 sm:h-96 sm:w-96" />
        <div className="absolute -bottom-40 -right-32 h-80 w-80 rotate-12 rounded-[4rem] border border-neutral-900/10 dark:border-white/10 sm:h-[28rem] sm:w-[28rem]" />
        <div className="absolute bottom-8 left-8 hidden items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.35em] text-neutral-900/35 dark:text-white/30 sm:flex">
          <span className="h-px w-10 bg-current" />
          Apparify Business
        </div>
      </div>

      <ThemeToggle
        variant="icon"
        className="fixed bottom-5 right-5 z-50 sm:bottom-6 sm:right-6"
      />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-[520px] items-center px-4 py-16">
        <div className="w-full rounded-2xl border border-white/80 bg-white/90 p-6 shadow-[0_24px_70px_-28px_rgba(0,0,0,0.35)] backdrop-blur-xl dark:border-white/10 dark:bg-neutral-900/85 dark:shadow-[0_24px_70px_-28px_rgba(0,0,0,0.9)]">
          <div className="mb-4">
            <img
              src={`${process.env.PUBLIC_URL}/apparify-icon.png`}
              alt="Apparify"
              className="mx-auto mb-3 h-14 w-14 object-contain"
            />
            <div className="text-lg text-center font-semibold tracking-tight">
              Business Login
            </div>
            <div className="mt-1 text-sm text-neutral-600 text-center dark:text-neutral-400">
              Owner • Manager • Staff access
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300">
                Email
              </label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@store.com"
                className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-3 py-3 text-sm outline-none
                           focus:border-neutral-400 focus:ring-2 focus:ring-neutral-200
                           dark:border-neutral-800 dark:bg-[#121214] dark:text-neutral-100 dark:focus:border-neutral-600 dark:focus:ring-neutral-800"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300">
                Password
              </label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                containerClassName="mt-1"
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-3 text-sm outline-none
                           focus:border-neutral-400 focus:ring-2 focus:ring-neutral-200
                           dark:border-neutral-800 dark:bg-[#121214] dark:text-neutral-100 dark:focus:border-neutral-600 dark:focus:ring-neutral-800"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-neutral-900 px-4 py-3 text-sm font-semibold text-white hover:bg-neutral-800 transition
                         dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-white
                         disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
